
import type { Team, WeaponClass } from '@cs-shot/battle-sim';
import skinAnchorsRaw from './skinAnchors.json';
import skinAnchorsSplitRaw from './skinAnchorsSplit.json';
import weaponAnchorsRaw from './weaponAnchors.json';
import weaponSkinsRaw from './weaponSkins.json';
import type { RAnchors } from './render/spriteRender';
import { WEAPON_REL_W, AWP_REL_W, WEAPON_GRIP } from './render/spriteRender';
import { bustCache } from './render/battleAssetVersion';

export interface Sprite {
  canvas: ImageBitmap | HTMLCanvasElement | HTMLImageElement;
  sx?: number;
  sy?: number;
  sw?: number;
  sh?: number;
  bx: number;
  by: number;
  bw: number;
  bh: number;
}

export type Stance = 'rifle' | 'awp';

export const TEAM_SKINS: Record<Team, string[]> = {
  t: ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 't9', 't10', 't11', 't12', 't13', 't14', 't15', 't16', 't17', 't18'],
  ct: ['ct1', 'ct2', 'ct3', 'ct4', 'ct5', 'ct6', 'ct7', 'ct8', 'ct9', 'ct10', 'ct11', 'ct12', 'ct13', 'ct14', 'ct15', 'ct16', 'ct17', 'ct18', 'ct19', 'ct20'],
};
const TEAMS: Team[] = ['t', 'ct'];

const SKIN_PARTS: { part: string; file: string }[] = [
  { part: 'rifle', file: 'chibi' },
  { part: 'awp', file: 'awp' },
  { part: 'dead', file: 'dead' },
];

const skinFile = (prefix: string, file: string) => `/battle/${prefix}_${file}.webp`;

export function allBodySources(): { key: string; src: string }[] {
  const out: { key: string; src: string }[] = [];
  TEAMS.forEach((team) => {
    for (const prefix of TEAM_SKINS[team]) {
      for (const { part, file } of SKIN_PARTS) {
        out.push({ key: `${prefix}_${part}`, src: skinFile(prefix, file) });
      }
    }
  });
  return out;
}

export function readySkins(team: Team): string[] {
  const ready = TEAM_SKINS[team].filter((s) => readyFor(s));
  return ready.length ? ready : TEAM_SKINS[team];
}

export function skinForFighter(team: Team, id: string): string {
  const pool = readySkins(team);
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return pool[Math.abs(h) % pool.length];
}

export function resolveSkin(team: Team, id: string, skin?: string | null): string {
  return skin && TEAM_SKINS[team].includes(skin) && readyFor(skin) ? skin : skinForFighter(team, id);
}
export const WEAPON_SRC: Record<string, string> = {
  ak: bustCache('/battle/weapon_ak.webp'),
  m4: bustCache('/battle/weapon_m4.webp'),
  awp: bustCache('/battle/weapon_awp.webp'),
  lmg: bustCache('/battle/weapon_lmg.webp'),
};

const WEAPON_SKINS: { name: string; key: string }[] = weaponSkinsRaw;
export function weaponSkinList(weaponKey: string): string[] {
  const base = WEAPON_SRC[weaponKey] ?? `/battle/weapon_${weaponKey}.webp`;
  const alts = WEAPON_SKINS.filter((s) => s.key === weaponKey).map((s) => `/battle/wpn_${s.name}.webp`);
  return [base, ...alts];
}
export function weaponSrcFor(weaponKey: string, skinIdx = 0): string {
  const list = weaponSkinList(weaponKey);
  return list[skinIdx] ?? list[0];
}
export function allWeaponSkinSources(): { name: string; src: string }[] {
  return WEAPON_SKINS.map((s) => ({ name: s.name, src: `/battle/wpn_${s.name}.webp` }));
}
export const GRENADE_SRC = bustCache('/battle/weapon_grenade.webp');
export const SMOKE_NADE_SRC = bustCache('/battle/smoke_nade.webp');
export const FLASH_NADE_SRC = bustCache('/battle/flash_nade.webp');
export const KNIFE_SRC = bustCache('/battle/weapon_knife.webp');

export const WEAPON_ICON_SRC: Record<string, string> = {
  ak: bustCache('/battle/icon_ak.webp'),
  m4: bustCache('/battle/icon_m4.webp'),
  awp: bustCache('/battle/icon_awp.webp'),
  lmg: bustCache('/battle/icon_lmg.webp'),
};
export const GRENADE_ICON_SRC = bustCache('/battle/icon_grenade.webp');
export const SMOKE_ICON_SRC = bustCache('/battle/icon_smoke.webp');
export const FLASH_ICON_SRC = bustCache('/battle/icon_flash.webp');
export const KNIFE_ICON_SRC = bustCache('/battle/icon_knife.webp');
export const MEDKIT_ICON_SRC = bustCache('/battle/icon_medkit.webp');
export const HEADSHOT_ICON_SRC = bustCache('/battle/icon_headshot.webp');
export const KNIFE_KILL_ICON_SRC = bustCache('/battle/icon_knife_kill.webp');
export const KNIFE_REL_W = 0.46;
export const KNIFE_GRIP = { x: 0.62, y: 0.58 };
export const KNIFE_ANGLE = 2.1;

export const HAND_BY_STANCE: Record<Stance, { x: number; y: number }> = {
  rifle: { x: 0.66, y: 0.56 },
  awp: { x: 0.82, y: 0.46 },
};

type AnchorOverride = { rifle?: { x: number; y: number }; awp?: { x: number; y: number }; scale?: number };
const SKIN_ANCHORS: Record<string, AnchorOverride> = skinAnchorsRaw;

export function handFor(skin: string | undefined, stance: Stance): { x: number; y: number } {
  const o = skin ? SKIN_ANCHORS[skin]?.[stance] : undefined;
  return o ?? HAND_BY_STANCE[stance];
}

export function scaleFor(skin: string | undefined): number {
  return (skin && (SKIN_SPLIT[skin]?.scale ?? SKIN_ANCHORS[skin]?.scale)) || 1;
}

type RunLike = { up?: { x?: number; y?: number; scale?: number }; scale?: number };
type SplitAnchor = {
  ready?: boolean;
  hand: RAnchors['hand'];
  up: RAnchors['up'];
  pivot: RAnchors['pivot'];
  scale?: number;
  deadScale?: number;
  body?: { rifle?: number; awp?: number };
  run?: { rifle?: RunLike; awp?: RunLike } | RunLike;
};
const SKIN_SPLIT: Record<string, SplitAnchor> = skinAnchorsSplitRaw;
const RUN_ONE = { up: { x: 0, y: 0.86, scale: 1 }, scale: 1 };
const runOf = (r: RunLike | undefined) => ({
  up: { x: r?.up?.x ?? RUN_ONE.up.x, y: r?.up?.y ?? RUN_ONE.up.y, scale: r?.up?.scale ?? RUN_ONE.up.scale },
  scale: r?.scale ?? RUN_ONE.scale,
});
const isPerStanceRun = (r: SplitAnchor['run']): r is { rifle?: RunLike; awp?: RunLike } =>
  !!r && ('rifle' in r || 'awp' in r);
const runRigOf = (run: SplitAnchor['run']): RAnchors['run'] => {
  if (isPerStanceRun(run)) {
    return { rifle: runOf(run.rifle), awp: runOf(run.awp) };
  }
  return { rifle: runOf(run), awp: runOf(run) };
};

const bothStances = (o: { rifle?: unknown; awp?: unknown } | undefined) => !!(o && o.rifle && o.awp);

export function readyFor(skin: string | undefined): boolean {
  return !!(skin && SKIN_SPLIT[skin]?.ready);
}

export function deadScaleFor(skin: string | undefined): number {
  return (skin && SKIN_SPLIT[skin]?.deadScale) || 1;
}

export function splitAnchorsFor(skin: string | undefined): RAnchors | null {
  const s = skin ? SKIN_SPLIT[skin] : undefined;
  if (!s || !bothStances(s.hand) || !bothStances(s.up) || !bothStances(s.pivot)) return null;
  return {
    hand: s.hand, up: s.up, pivot: s.pivot, scale: s.scale ?? 1,
    body: { rifle: s.body?.rifle ?? 1, awp: s.body?.awp ?? 1 },
    run: runRigOf(s.run),
  };
}

const SPLIT_PARTS = ['legs', 'upper_rifle', 'upper_awp', 'legs_run'] as const;
export function splitBodySources(): { key: string; src: string }[] {
  const out: { key: string; src: string }[] = [];
  TEAMS.forEach((team) => {
    for (const prefix of TEAM_SKINS[team]) {
      for (const part of SPLIT_PARTS) out.push({ key: `${prefix}_${part}`, src: skinFile(prefix, part) });
    }
  });
  return out;
}
export { WEAPON_REL_W, AWP_REL_W, WEAPON_GRIP };

const WEAPON_ANCHORS: Record<string, { x: number; y: number }> = weaponAnchorsRaw;
export function weaponMuzzleFor(weaponKey: string): { x: number; y: number } {
  return WEAPON_ANCHORS[weaponKey] ?? { x: 1, y: WEAPON_GRIP.y };
}

export const TOKEN_H = 244;
export const CORPSE_REL = 1.15;
export const EXPLOSION_FRAMES = 6;

export const stanceOf = (w: WeaponClass): Stance => (w === 'awp' ? 'awp' : 'rifle');
export const weaponKeyOf = (team: Team, w: WeaponClass) =>
  w === 'awp' ? 'awp' : w === 'lmg' ? 'lmg' : team === 't' ? 'ak' : 'm4';

export function toBitmap<T extends HTMLImageElement | HTMLCanvasElement>(src: T): Promise<ImageBitmap | T> {
  if (typeof createImageBitmap !== 'function') return Promise.resolve(src);
  return createImageBitmap(src).catch(() => src);
}

// WHY: canvas только транзиентно для alpha-скана — ~300 удержанных canvas превышали бюджет iOS WebView, WebKit чистил их
// молча
export function loadKeyedSprite(src: string): Promise<Sprite> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // WHY: getContext/getImageData могут вернуть null или бросить на memory-starved Android WebView — reject вместо uncaught
      // throw
      try {
        const c = document.createElement('canvas');
        c.width = img.width;
        c.height = img.height;
        const cx = c.getContext('2d', { willReadFrequently: true });
        if (!cx) { reject(new Error(`no 2d context for ${src}`)); return; }
        cx.drawImage(img, 0, 0);
        const px = cx.getImageData(0, 0, c.width, c.height).data;
        let minX = c.width, minY = c.height, maxX = 0, maxY = 0;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] > 24) {
            const p = i / 4;
            const x = p % c.width;
            const y = (p / c.width) | 0;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
        c.width = 0;
        c.height = 0;
        void toBitmap(img).then((bmp) => resolve({ canvas: bmp, bx: minX, by: minY, bw: maxX - minX, bh: maxY - minY }));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(`sprite decode failed: ${src}`));
      }
    };
    img.onerror = () => reject(new Error(`failed to load ${src}`));
    img.src = bustCache(src);
  });
}

